import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search as SearchIcon } from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColDef,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  ModuleRegistry,
  PaginationModule,
  QuickFilterModule,
  RowSelectionModule,
  RowStyleModule,
  SelectionChangedEvent,
  TextFilterModule,
  ValidationModule,
} from 'ag-grid-community';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import projectsService from '../services/projectsService';
import { ClientFeedbackListItem, ProjectType } from '../types';
import ErrorBoundary from '../components/ErrorBoundary';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ValidationModule,
  QuickFilterModule,
  PaginationModule,
  RowStyleModule,
  TextFilterModule,
  RowSelectionModule,
  CellStyleModule,
]);

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };

const RATING_KEYS = [
  'overall_satisfaction',
  'technical_quality',
  'communication_responsiveness',
  'schedule_timeliness',
  'understanding_requirements',
] as const;

type RatingKey = (typeof RATING_KEYS)[number];

const RATING_LABELS: Record<RatingKey, string> = {
  overall_satisfaction: 'Overall Satisfaction',
  technical_quality: 'Technical Quality of Deliverables',
  communication_responsiveness: 'Communication & Responsiveness',
  schedule_timeliness: 'Schedule & Timeliness',
  understanding_requirements: 'Understanding of Requirements',
};

const HEALTH_COLORS = {
  green: '#2e7d32',
  orange: '#ed6c02',
  red: '#d32f2f',
} as const;

const CHART_N_COLOR = '#1a5276';
const CHART_N1_COLOR = '#e67e22';

const AVERAGE_ROW_ID = '__average__';

type FeedbackGridRow = ClientFeedbackListItem & { isAverageRow?: boolean };

const isClosedStatus = (status?: string | null) =>
  (status || '').trim().toLowerCase() === 'closed';

const hasFeedback = (row: Pick<ClientFeedbackListItem, 'feedback_id'>) =>
  row.feedback_id != null;

function validRatings(row: Pick<ClientFeedbackListItem, RatingKey>): number[] {
  return RATING_KEYS.map((k) => row[k]).filter((v): v is number => v != null && v >= 1 && v <= 4);
}

function computeMean(row: Pick<ClientFeedbackListItem, RatingKey>): number | null {
  const values = validRatings(row);
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function feedbackIndicator(mean: number | null): keyof typeof HEALTH_COLORS | null {
  if (mean == null) return null;
  if (mean >= 3) return 'green';
  if (mean >= 2) return 'orange';
  return 'red';
}

function avgField(rows: ClientFeedbackListItem[], key: RatingKey): number | null {
  const values = rows.map((r) => r[key]).filter((v): v is number => v != null && v >= 1 && v <= 4);
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function avgMean(rows: ClientFeedbackListItem[]): number | null {
  const means = rows.map(computeMean).filter((m): m is number => m != null);
  if (means.length === 0) return null;
  return means.reduce((a, b) => a + b, 0) / means.length;
}

function buildAverageRow(sourceRows: ClientFeedbackListItem[]): FeedbackGridRow {
  const avg = (key: RatingKey) => {
    const v = avgField(sourceRows, key);
    return v != null ? Math.round(v * 10) / 10 : null;
  };
  return {
    project_id: -1,
    nd_number: null,
    name: '',
    client_name: null,
    office_code: null,
    office_name: null,
    office: null,
    status: null,
    year: null,
    project_type_id: null,
    feedback_id: -1,
    overall_satisfaction: avg('overall_satisfaction'),
    technical_quality: avg('technical_quality'),
    communication_responsiveness: avg('communication_responsiveness'),
    schedule_timeliness: avg('schedule_timeliness'),
    understanding_requirements: avg('understanding_requirements'),
    submitted_at: null,
    isAverageRow: true,
  };
}

const IndicatorCell: React.FC<ICellRendererParams<FeedbackGridRow>> = (params) => {
  const mean = computeMean(params.data ?? ({} as ClientFeedbackListItem));
  const indicator = feedbackIndicator(mean);
  if (!indicator) return null;
  const color = HEALTH_COLORS[indicator];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: 11,
          height: 11,
          borderRadius: '50%',
          bgcolor: color,
          boxShadow: `0 0 0 2px ${color}33`,
        }}
      />
    </Box>
  );
};

const ClientFeedbackPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const [items, setItems] = useState<ClientFeedbackListItem[]>([]);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickFilter, setQuickFilter] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState<number | ''>('');
  const [showClosed, setShowClosed] = useState(true);
  const [withFeedbackOnly, setWithFeedbackOnly] = useState(false);
  const [activeYear, setActiveYear] = useState(() => new Date().getFullYear());
  const [gridApi, setGridApi] = useState<GridApi<FeedbackGridRow> | null>(null);
  const [visibleRows, setVisibleRows] = useState<ClientFeedbackListItem[]>([]);
  const [pinnedTopRowData, setPinnedTopRowData] = useState<FeedbackGridRow[]>([]);
  const gridApiRef = useRef<GridApi<FeedbackGridRow> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void projectsService
      .listAllCompletionFeedback()
      .then((data) => {
        if (!cancelled) setItems(data.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load client feedback');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    projectsService
      .getProjectTypes()
      .then((types) => {
        if (!cancelled) setProjectTypes(types);
      })
      .catch(() => {
        /* type filter still works without labels */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredItems = useMemo(() => {
    let result = items;
    if (!showClosed) {
      result = result.filter((project) => !isClosedStatus(project.status));
    }
    if (projectTypeFilter !== '') {
      result = result.filter((project) => project.project_type_id === projectTypeFilter);
    }
    if (withFeedbackOnly) {
      result = result.filter(hasFeedback);
    }
    return result;
  }, [items, showClosed, projectTypeFilter, withFeedbackOnly]);

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    const current = new Date().getFullYear();
    years.add(current);
    years.add(current - 1);
    items.forEach((i) => {
      if (i.year != null) years.add(i.year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [items]);

  const refreshDerivedRows = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;

    const selected = api.getSelectedRows().filter((r) => !r.isAverageRow);
    let sourceRows: ClientFeedbackListItem[];
    if (selected.length > 0) {
      sourceRows = selected;
    } else {
      sourceRows = [];
      api.forEachNodeAfterFilter((node) => {
        if (node.data && !node.data.isAverageRow) {
          sourceRows.push(node.data);
        }
      });
    }

    setVisibleRows(sourceRows);
    setPinnedTopRowData([buildAverageRow(sourceRows.filter(hasFeedback))]);
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<FeedbackGridRow>) => {
      gridApiRef.current = params.api;
      setGridApi(params.api);
      refreshDerivedRows();
    },
    [refreshDerivedRows],
  );

  const onFilterChanged = useCallback(
    (_event: FilterChangedEvent<FeedbackGridRow>) => {
      refreshDerivedRows();
    },
    [refreshDerivedRows],
  );

  const onSelectionChanged = useCallback(
    (_event: SelectionChangedEvent<FeedbackGridRow>) => {
      refreshDerivedRows();
    },
    [refreshDerivedRows],
  );

  useEffect(() => {
    if (!gridApi) return;
    const frame = requestAnimationFrame(() => refreshDerivedRows());
    return () => cancelAnimationFrame(frame);
  }, [filteredItems, quickFilter, gridApi, refreshDerivedRows]);

  const chartRows = useMemo(
    () => visibleRows.filter(hasFeedback),
    [visibleRows],
  );

  const chartData = useMemo(() => {
    const nRows = chartRows.filter((r) => r.year === activeYear);
    const n1Rows = chartRows.filter((r) => r.year === activeYear - 1);

    const categories: Array<{ key: RatingKey | 'mean'; label: string }> = [
      ...RATING_KEYS.map((k) => ({ key: k as RatingKey | 'mean', label: RATING_LABELS[k] })),
      { key: 'mean', label: 'Mean rating' },
    ];

    return categories.map(({ key, label }) => {
      if (key === 'mean') {
        return {
          category: label,
          N: avgMean(nRows),
          'N-1': avgMean(n1Rows),
        };
      }
      return {
        category: label,
        N: avgField(nRows, key),
        'N-1': avgField(n1Rows, key),
      };
    });
  }, [chartRows, activeYear]);

  const formatRating = useCallback((value: number | null | undefined) => {
    if (value == null) return '-';
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }, []);

  const columnDefs = useMemo<ColDef<FeedbackGridRow>[]>(() => {
    const ratingCol = (field: RatingKey, headerName: string): ColDef<FeedbackGridRow> => ({
      colId: field,
      field,
      headerName,
      width: 120,
      minWidth: 100,
      flex: 1,
      sortable: true,
      valueFormatter: (params) => formatRating(params.value),
      cellStyle: (params) => {
        if (params.data?.isAverageRow) {
          return { fontStyle: 'italic', color: HEALTH_COLORS.red, fontWeight: 600 };
        }
        return undefined;
      },
    });

    return [
      {
        colId: 'nd_number',
        headerName: 'ND number',
        field: 'nd_number',
        width: 140,
        minWidth: 120,
        flex: 1.2,
        sortable: true,
        checkboxSelection: (params) => !params.data?.isAverageRow,
        headerCheckboxSelection: true,
        showDisabledCheckboxes: true,
        valueFormatter: (params) => (params.data?.isAverageRow ? '' : params.value || '-'),
      },
      {
        colId: 'name',
        headerName: 'Name',
        field: 'name',
        width: 160,
        minWidth: 120,
        flex: 1.4,
        sortable: true,
        valueFormatter: (params) => (params.data?.isAverageRow ? '' : params.value || '-'),
      },
      {
        colId: 'client_name',
        headerName: 'Customer',
        field: 'client_name',
        width: 160,
        minWidth: 120,
        flex: 1.4,
        sortable: true,
        filter: true,
        valueFormatter: (params) => (params.data?.isAverageRow ? '' : params.value || '-'),
      },
      {
        colId: 'office',
        headerName: 'Office',
        field: 'office_code',
        width: 90,
        minWidth: 80,
        flex: 0.8,
        sortable: true,
        filter: true,
        valueGetter: (params) => {
          if (params.data?.isAverageRow) return '';
          return params.data?.office_code || params.data?.office_name || params.data?.office || '-';
        },
      },
      {
        colId: 'status',
        headerName: 'Status',
        field: 'status',
        width: 110,
        minWidth: 90,
        flex: 1,
        sortable: true,
        filter: true,
        valueFormatter: (params) => (params.data?.isAverageRow ? '' : params.value || '-'),
      },
      {
        colId: 'year',
        headerName: 'Year',
        field: 'year',
        width: 90,
        minWidth: 70,
        flex: 0.7,
        sortable: true,
        filter: true,
        valueFormatter: (params) => (params.data?.isAverageRow ? '' : (params.value ?? '-')),
      },
      ratingCol('overall_satisfaction', 'Overall Satisfaction'),
      ratingCol('technical_quality', 'Technical Quality of Deliverables'),
      ratingCol('communication_responsiveness', 'Communication & Responsiveness'),
      ratingCol('schedule_timeliness', 'Schedule & Timeliness'),
      ratingCol('understanding_requirements', 'Understanding of Requirements'),
      {
        colId: 'mean_rating',
        headerName: 'Mean rating',
        width: 110,
        minWidth: 90,
        flex: 0.9,
        sortable: true,
        valueGetter: (params) => computeMean(params.data ?? ({} as ClientFeedbackListItem)),
        valueFormatter: (params) => formatRating(params.value),
        cellStyle: (params) => {
          const mean = params.value as number | null;
          if (params.data?.isAverageRow) {
            return { fontStyle: 'italic', color: HEALTH_COLORS.red, fontWeight: 600 };
          }
          if (mean != null && mean < 3) {
            return { color: HEALTH_COLORS.orange, fontWeight: 600 };
          }
          return undefined;
        },
      },
      {
        colId: 'indicator',
        headerName: 'Indicator',
        width: 90,
        minWidth: 80,
        flex: 0.7,
        sortable: false,
        cellRenderer: IndicatorCell,
      },
    ];
  }, [formatRating]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: false,
      suppressMovable: false,
    }),
    [],
  );

  const pageFillSx = {
    height: `calc(100vh - ${theme.spacing(10)} - ${theme.spacing(4)})`,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
  } as const;

  if (loading) {
    return (
      <Box sx={pageFillSx}>
        <Skeleton variant="rectangular" height="100%" />
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <Box sx={pageFillSx}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 2 }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
            }}
          >
            Client Feedback
          </Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="client-feedback-year-label">Active year (N)</InputLabel>
            <Select
              labelId="client-feedback-year-label"
              label="Active year (N)"
              value={activeYear}
              onChange={(e) => setActiveYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {error}
          </Alert>
        )}

        {!error && items.length === 0 && (
          <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
            No projects were found.
          </Alert>
        )}

        {items.length > 0 && (
          <Paper
            sx={{
              p: 3,
              mb: 2,
              borderRadius: 1,
              boxShadow: 'none',
              border: `1px solid ${panelBorder}`,
              bgcolor: surfaceBg,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body2"
              gutterBottom
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.7rem',
                color: isDark ? '#b0bec5' : '#546e7a',
                textAlign: 'center',
              }}
            >
              Client feedback indicator
            </Typography>
            <Box height={280} width="100%" mt={1}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 16, right: 24, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? '#2c3e50' : '#eee'} />
                  <XAxis
                    dataKey="category"
                    tick={{ fontSize: 11, fill: isDark ? '#b0bec5' : '#666' }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis
                    domain={[0, 4]}
                    ticks={[0, 1, 2, 3, 4]}
                    interval={0}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: isDark ? '#b0bec5' : '#666' }}
                  />
                  <RechartsTooltip
                    cursor={{ fill: 'transparent' }}
                    contentStyle={{
                      borderRadius: 4,
                      border: `1px solid ${panelBorder}`,
                      boxShadow: 'none',
                      backgroundColor: surfaceBg,
                    }}
                    formatter={(value: number | undefined) =>
                      value != null ? value.toFixed(2) : '—'
                    }
                  />
                  <Legend
                    verticalAlign="bottom"
                    wrapperStyle={{ paddingTop: 16 }}
                  />
                  <Bar dataKey="N-1" name="N-1" fill={CHART_N1_COLOR} barSize={28} />
                  <Bar dataKey="N" name="N" fill={CHART_N_COLOR} barSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
            <TextField
              size="small"
              placeholder="Quick filter projects..."
              value={quickFilter}
              onChange={(e) => setQuickFilter(e.target.value)}
              sx={{ flex: 1, maxWidth: 400, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="client-feedback-project-type-filter-label">Project type</InputLabel>
              <Select
                labelId="client-feedback-project-type-filter-label"
                label="Project type"
                value={projectTypeFilter === '' ? '' : String(projectTypeFilter)}
                onChange={(e) => {
                  const v = e.target.value;
                  setProjectTypeFilter(v === '' ? '' : Number(v));
                }}
              >
                <MenuItem value="">All types</MenuItem>
                {projectTypes.map((pt) => (
                  <MenuItem key={pt.id} value={String(pt.id)}>
                    {pt.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showClosed}
                  onChange={(e) => setShowClosed(e.target.checked)}
                />
              }
              label="Show closed"
              sx={{ ml: 0.5, mr: 0, userSelect: 'none' }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={withFeedbackOnly}
                  onChange={(e) => setWithFeedbackOnly(e.target.checked)}
                />
              }
              label="With feedback"
              sx={{ ml: 0.5, mr: 0, userSelect: 'none' }}
            />
          </Box>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              border: `1px solid ${panelBorder}`,
              borderRadius: 1,
              overflow: 'hidden',
            }}
            className={isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
          >
            <AgGridReact<FeedbackGridRow>
              theme="legacy"
              rowData={filteredItems}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pinnedTopRowData={pinnedTopRowData}
              getRowId={(params) =>
                params.data.isAverageRow
                  ? AVERAGE_ROW_ID
                  : String(params.data.project_id)
              }
              quickFilterText={quickFilter}
              rowHeight={45}
              pagination
              paginationPageSize={20}
              rowSelection={{ mode: 'multiRow', enableClickSelection: false }}
              onGridReady={onGridReady}
              onFilterChanged={onFilterChanged}
              onSelectionChanged={onSelectionChanged}
              onRowClicked={(event) => {
                if (event.data?.isAverageRow || !event.data?.project_id) return;
                navigate(`/projects/${event.data.project_id}/documents?tab=feedback`);
              }}
              getRowStyle={(params) => {
                if (params.data?.isAverageRow) {
                  return { fontWeight: 500, backgroundColor: isDark ? '#1a2d42' : '#fafafa' };
                }
                return { cursor: 'pointer' };
              }}
            />
          </Paper>
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default ClientFeedbackPage;
